import React, { useState } from 'react'

import { createRoot } from 'react-dom/client'
import { useTranslation } from 'react-i18next'

import { AvatarPessoa } from '@/components/avatar-pessoa'
import {
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialog as AlertDialogPrimitive,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { StringUtils } from '@/utils'

interface AlertDialogOptions {
  duracao: `${string}:${string}:${string}`
  nome: string
  imagem?: string | null
}

const AlertDialogComponent: React.FC<{
  options: AlertDialogOptions
  onAction: VoidFunction
}> = ({ options, onAction }) => {
  const { t } = useTranslation('atendimento')
  const [open, setOpen] = useState(true)

  const handleAction = () => {
    setOpen(false)
    onAction()
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) onAction()
  }

  return (
    <AlertDialogPrimitive open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md gap-0 p-0">
        <AlertDialogHeader className="p-6">
          <AlertDialogTitle className="text-xl font-medium text-gray-900">
            {t('components.dialogs.atendimento_encerrado.titulo')}
          </AlertDialogTitle>
          <AlertDialogDescription className="font-nunito text-sm text-neutral-600 [&>span]:text-neutral-800">
            {t('components.dialogs.atendimento_encerrado.tempo')}
            <span>{options.duracao}</span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="border-muted border">
          <div className="flex items-center gap-2 p-4">
            <AvatarPessoa nome={options.nome} imagem={options.imagem} />
            <div className="flex-1 space-y-1 overflow-hidden">
              <span className="line-clamp-2 text-base font-medium">{StringUtils.capitalize(options.nome)}</span>
            </div>
          </div>
        </div>
        <AlertDialogFooter className="flex justify-end p-6">
          <AlertDialogAction onClick={handleAction}>
            {t('components.dialogs.atendimento_encerrado.fechar')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialogPrimitive>
  )
}

const createDialogAtendimentoEncerrado = () => {
  return {
    show: (options: AlertDialogOptions): Promise<void> => {
      return new Promise((resolve) => {
        const container = document.createElement('div')
        document.body.appendChild(container)
        const root = createRoot(container)
        const handleAction = () => {
          resolve()
          root.unmount()
          container.remove()
        }

        root.render(<AlertDialogComponent options={options} onAction={handleAction} />)
      })
    },
  }
}

export const DialogAtendimentoEncerrado = createDialogAtendimentoEncerrado()
